import React from "react";
import { NoDataAvailableDiv } from "../../../components/NoDataAvailableDiv";
import RecipientsRow from "./RecipientsRow";
import { DeleteDialog, Modal } from "components";
import EditParticipantAsContact from "eSign/components/modals/EditParticipantAsContact";
import { SetParticipantFromContactParams } from "eSign/components/Participant/hooks";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { getCultureFromLcid } from "utilities/enumutils";
import { contactCacheKey, contactSmallV2CacheKey } from "utilities/api/contactQuery";

interface Props {
  viewers: server.dto.InvoiceParticipant[]
  invoice: server.dto.InvoiceV2
  onChanged: () => Promise<any>
}

const RecipientsList = (props: Props) => {
  const queryClient = useQueryClient();
  const [editingRecipient, setEditingRecipient] = React.useState<server.dto.InvoiceParticipant>(undefined);
  const [deletingParticipant, setDeletingParticipant] = React.useState<server.dto.InvoiceParticipant>(undefined);

  const onDelete = async (participant: server.dto.InvoiceParticipant) => {
    await window.CoreApi.InvoiceV2.DeleteParticipant({ invoiceParticipantId: participant.invoiceParticipantId });
    await props.onChanged();
    setDeletingParticipant(undefined);
  };

  const saveViewerMutation = useMutation({
    mutationFn: async ({ email, contactId, culture, invitationBody, invitationSubject }: SetParticipantFromContactParams) => {
      const res = await window.CoreApi.InvoiceV2.UpdateParticipant({
        invoiceParticipantId: editingRecipient.invoiceParticipantId,
        contactId,
        emailAddress: email,
        contactCulture: culture,
        invitationBody,
        invitationSubject,
      });

      if (!res.data.hasErrors) {
        setEditingRecipient(undefined);
        await queryClient.invalidateQueries(contactCacheKey(contactId));
        await queryClient.invalidateQueries(contactSmallV2CacheKey(contactId));
        props.onChanged();
        return;
      }

      return res.data.errors[0].message;
    }
  })

  if (props.viewers.length === 0) {
    return <NoDataAvailableDiv />;
  }

  return <>
    <div className="list-with-gaps card-layout">
      <div className="list-with-gaps-body">
        {props.viewers.map((participant) => (
          <RecipientsRow
            lifeCycle={props.invoice.lifeCycle}
            participant={participant}
            key={participant.invoiceParticipantId}
            onDeleteClicked={async () => setDeletingParticipant(participant)}
            onEditClicked={() => { setEditingRecipient(participant); }}
          />
        ))}
      </div>
    </div>

    <DeleteDialog
      isOpen={deletingParticipant != null}
      message={RESX.GeneralWarnings.AreYouSure}
      handleClose={() => { setDeletingParticipant(undefined); }}
      onDelete={() => onDelete(deletingParticipant)}
    />

    <Modal
      isOpen={editingRecipient != null}
      handleClose={() => { setEditingRecipient(null); }}
      title={"Recipient"}
      width="small"
    >
      <EditParticipantAsContact
        contactName={editingRecipient?.contact?.name?.fullName}
        contactId={editingRecipient?.contact?.contactId}
        initialEmail={editingRecipient?.email}
        initialCompany={null}
        initialCulture={editingRecipient?.contact?.culture ? getCultureFromLcid(editingRecipient.contact.culture) : null}

        initialInvitationSubject={editingRecipient?.invitationSubject}
        initialInvitationBody={editingRecipient?.invitationBody}
        saveMutation={saveViewerMutation}
        isViewer
        messageTemplateType={"invitationForInvoiceV2"}
      />
    </Modal>
  </>;
};

export default RecipientsList;